import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { api, API_BASE } from "../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import {
  Users, Pulse, CurrencyInr, Leaf, ShieldCheck, Download, TrendUp,
} from "@phosphor-icons/react";

const COLORS = ["#2D5A27", "#C25D38", "#D4A418", "#6E8A4A", "#8A6E4A"];

export default function Analytics() {
  const [ov, setOv] = useState(null);
  const [ops, setOps] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cust, setCust] = useState(null);
  const [waste, setWaste] = useState(null);
  const [comp, setComp] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/operations?days=30"),
      api.get("/analytics/staff"),
      api.get("/analytics/customer"),
      api.get("/analytics/waste"),
      api.get("/analytics/compliance"),
    ]).then(([a, b, c, d, e, f]) => {
      setOv(a.data); setOps(b.data); setStaff(c.data);
      setCust(d.data); setWaste(e.data); setComp(f.data);
    }).catch(() => {});
  }, []);

  const exportCsv = async (report) => {
    const token = localStorage.getItem("rescigo_token");
    const r = await fetch(`${API_BASE}/analytics/export?report=${report}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${report}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell title="Analytics & Reports" subtitle="Module 13"
      actions={
        <div className="flex gap-2">
          {["customers", "invoices", "tasks"].map(r => (
            <button key={r} onClick={() => exportCsv(r)} data-testid={`export-${r}`}
                    className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
              <Download size={12}/>{r} CSV
            </button>
          ))}
        </div>
      }
    >
      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="analytics-kpis">
        <Kpi label="Customers" value={ov?.customers} sub={`${ov?.approved_customers || 0} approved`} icon={Users}/>
        <Kpi label="Tasks completed" value={ov?.tasks_completed} sub={`${ov?.completion_rate || 0}% rate`} icon={Pulse}/>
        <Kpi label="Revenue" value={ov ? `₹${(ov.revenue/1000).toFixed(1)}k` : "—"} sub={`${ov?.tickets_open || 0} open tickets`} icon={CurrencyInr} accent/>
        <Kpi label="Waste processed" value={ov ? `${ov.waste_kg} kg` : "—"} sub={`${ov?.alerts_open || 0} alerts`} icon={Leaf}/>
      </div>

      {/* Ops trend */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-6 mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Last 30 days</div>
        <div className="font-heading text-lg mb-4">Operations trend</div>
        <div className="h-72 min-h-[288px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ops}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9"/>
              <XAxis dataKey="date" stroke="#5C5A55" fontSize={10}/>
              <YAxis stroke="#5C5A55" fontSize={11}/>
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 6, fontSize: 12 }}/>
              <Line type="monotone" dataKey="completed" stroke="#2D5A27" strokeWidth={2}/>
              <Line type="monotone" dataKey="missed" stroke="#C25D38" strokeWidth={2}/>
              <Line type="monotone" dataKey="pending" stroke="#D4A418" strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
        {ops.length === 0 && <div className="text-xs text-[#8A8780] mt-3">No task data in range.</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Staff performance */}
        <div className="bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Staff performance</div>
            <div className="font-heading text-lg">Tasks by staff</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Staff</th>
                <th className="px-6 py-3 text-right">Done</th>
                <th className="px-6 py-3 text-right">Missed</th>
                <th className="px-6 py-3 text-right">Efficiency</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 && <tr><td colSpan={4} className="px-6 py-8 text-center text-[#8A8780]">No staff data.</td></tr>}
              {staff.map(s => (
                <tr key={s.staff_id}>
                  <td className="px-6 py-3"><span className="font-medium">{s.name}</span><div className="text-xs text-[#8A8780]">{s.role}</div></td>
                  <td className="px-6 py-3 font-mono text-right text-[#2D5A27]">{s.completed}</td>
                  <td className="px-6 py-3 font-mono text-right text-[#C25D38]">{s.missed}</td>
                  <td className="px-6 py-3 font-mono text-right font-semibold">{s.efficiency}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Waste breakdown */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Waste & CO₂</div>
              <div className="font-heading text-lg">Category breakdown</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">CO₂ saved</div>
              <div className="font-heading text-2xl text-[#2D5A27]">{waste?.total_co2_kg || 0} kg</div>
            </div>
          </div>
          <div className="h-56 min-h-[224px] mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={waste?.breakdown || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9"/>
                <XAxis dataKey="waste_name" stroke="#5C5A55" fontSize={9}/>
                <YAxis stroke="#5C5A55" fontSize={11}/>
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", borderRadius: 6, fontSize: 12 }}/>
                <Bar dataKey="kg" fill="#2D5A27" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {(!waste?.breakdown || waste.breakdown.length === 0) && <div className="text-xs text-[#8A8780]">No waste records yet.</div>}
        </div>
      </div>

      {/* Compliance */}
      {comp && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="compliance-grid">
          <Kpi label="Service compliance" value={`${comp.service_compliance_pct}%`} icon={ShieldCheck} accent/>
          <Kpi label="Missed tasks" value={`${comp.missed_task_pct}%`} icon={Pulse} danger={comp.missed_task_pct > 5}/>
          <Kpi label="Manual QR %" value={`${comp.manual_scan_pct}%`} icon={Pulse}/>
          <Kpi label="Reconcile mismatch" value={`${comp.reconciliation_mismatch_pct}%`} icon={ShieldCheck} danger={comp.reconciliation_mismatch_pct > 3}/>
          <Kpi label="Fraud alerts" value={comp.total_fraud_alerts} icon={ShieldCheck} danger={comp.total_fraud_alerts > 0}/>
        </div>
      )}
    </AppShell>
  );
}

const Kpi = ({ label, value, sub, icon: Icon, accent, danger }) => (
  <div className="stat-card">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded flex items-center justify-center ${danger ? "bg-[#C25D38]/10" : accent ? "bg-[#2D5A27]/10" : "bg-[#F7F6F2]"}`}>
        <Icon size={16} color={danger ? "#C25D38" : "#2D5A27"} weight="duotone"/>
      </div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
    </div>
    <div className="font-heading text-3xl mt-4">{value ?? "—"}</div>
    {sub && <div className="text-xs text-[#8A8780] mt-1">{sub}</div>}
  </div>
);
